/**
 * Vellatini cart suggest — drawer "With it +" (owner rulings 2026-09-04).
 * Every eligible cart line renders its offer as a <details class="dw-suggest">
 * directly under itself (snippets/vellatini-cart-suggest-row.liquid, picked
 * and deduped in snippets/cart-products.liquid) plus a "With it +" trigger in
 * its meta row ([data-vt-withit-trigger]); this module opens the matching
 * offer. Offers never collapse each other, and after a cart mutation the
 * server's primary offer is re-opened if the morph left none open.
 *
 * Pure UI toggle — no state is read or written. Rule 11 ("server always
 * wins") still holds: every offer carries declarative-open, so the next cart
 * mutation's re-render overrides whatever a shopper toggled here. Loaded from
 * cart-products.liquid, nowhere else.
 */

/**
 * @param {MouseEvent} event
 */
function handleWithItTriggerClick(event) {
  const trigger = event.target instanceof Element ? event.target.closest('[data-vt-withit-trigger]') : null;
  if (!trigger) return;

  const targetId = trigger.getAttribute('data-vt-withit-trigger');
  const target = targetId ? document.getElementById(targetId) : null;
  if (!(target instanceof HTMLDetailsElement) || target.open) return;

  event.preventDefault();

  // Owner 2026-09-04: opening one line's offer never collapses another
  // (no exclusive details[name] group any more).
  target.open = true;
}

document.addEventListener('click', handleWithItTriggerClick);

/**
 * Offer "Add" (snippets/vellatini-cart-suggest-row.liquid). The row lives
 * inside the cart's own <form>, so it cannot carry a product form; the add is
 * posted through vellatini-pair-add.js, which walks Horizon's cart-update
 * event path (auto-open, section morph, cart icon) exactly like "Add both".
 * The button's [disabled] state is the loading state (vellatini-tokens.css).
 * @param {MouseEvent} event
 */
async function handleOfferAddClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-vt-offer-add] button') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  const host = button.closest('[data-vt-offer-add]');
  if (!(host instanceof HTMLElement) || !host.dataset.addModule) return;

  event.preventDefault();
  button.disabled = true;
  try {
    const add = await import(host.dataset.addModule);
    const preorder = host.dataset.preorderBatch
      ? { batch: host.dataset.preorderBatch, eta: host.dataset.preorderEta || '' }
      : null;
    // Attribution: added from the drawer offer under the `data-line-handle` bag.
    const properties = {
      ...(add.partnerLineProperties(preorder) || {}),
      ...add.sourceLineProperties('cart_suggest', host.dataset.lineHandle),
    };
    const items = [{ id: Number(host.dataset.variantId), quantity: 1, properties }];
    // Not the cart-items-component itself: its handler ignores events whose
    // target is itself (component-cart-items.js #handleCartUpdate).
    const dispatchOn = button.closest('cart-drawer-component') || document;
    const result = await add.addBoth({ items, dispatchOn, productId: host.dataset.productId });
    if (!result.ok) console.warn('[vellatini-cart-suggest] add failed', result.message);
  } catch (error) {
    console.error('[vellatini-cart-suggest]', error);
  } finally {
    button.disabled = false;
  }
}

document.addEventListener('click', handleOfferAddClick);

/**
 * After a cart mutation the drawer is morphed (assets/morph.js) and can come
 * back with no offer open even though the server marked a primary (`open` +
 * data-vt-primary on the most recent line's offer). Re-assert it: if no offer
 * is open once the DOM settles, open the server's primary.
 */
function ensurePrimaryOpen() {
  const offers = document.querySelectorAll('.dw-suggest-row details.dw-suggest');
  if (!offers.length) return;
  if (Array.prototype.some.call(offers, (d) => d.open)) return;
  const primary = document.querySelector('.dw-suggest-row details[data-vt-primary]');
  if (primary) primary.open = true;
}

let ensureFrame = 0;
function scheduleEnsurePrimaryOpen() {
  cancelAnimationFrame(ensureFrame);
  ensureFrame = requestAnimationFrame(ensurePrimaryOpen);
}

const drawerHost = document.querySelector('cart-drawer-component');
if (drawerHost) {
  new MutationObserver(scheduleEnsurePrimaryOpen).observe(drawerHost, { childList: true, subtree: true });
}
